/*
 * Copyright (c) 2023. Selldone® Business OS™
 *
 * Author: M.Pajuhaan
 * Web: https://selldone.com
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * All rights reserved. In the weave of time, where traditions and innovations intermingle, this content was crafted.
 * From the essence of thought, through the corridors of creativity, each word, and sentiment has been molded.
 * Not just to exist, but to inspire. Like an artist's stroke or a sculptor's chisel, every nuance is deliberate.
 * Our journey is not just about reaching a destination, but about creating a masterpiece.
 * Tread carefully, for you're treading on dreams.
 */

import adaIcon from "./assets/currency/ADA.png";
import btcIcon from "./assets/currency/currency_bitcoin.svg";
import ethIcon from "./assets/currency/ethereum.svg";
import nioIcon from "./assets/currency/nio-token.svg";
import selldoneIcon from "./assets/currency/samin-token.svg";
import solanaIcon from "./assets/currency/solana.svg";

/**
 * Represents the details of a currency.
 *
 * @typedef {object} ICurrency
 *
 * @property {string} code - The unique identifier for the currency (e.g. 'USD', 'EUR').
 * @property {boolean} enable - Indicates whether the currency is enabled or not.
 * @property {string} name - The name of the currency (e.g. 'global.currency.USD').
 * @property {string} [alt_name] - An alternative or localized name for the currency, if available.
 * @property {number} [alt_factor] - An alternative or localized factor for the currency, if available. like 0.1
 * @property {string} country - The country associated with the currency (e.g. 'USA', 'Euro').
 * @property {string} [flag] - The flag or code representing the country (e.g. 'US', 'EU').
 * @property {string} [icon] - The path to the currency's icon, if available.
 * @property {number} floats - The number of decimal places for the currency (commonly 2 for most currencies).
 * @property {string} format - The format string to represent the currency (e.g. '0,0.[00]').
 * @property {number} round_factor - The factor used for rounding the currency value.
 * @property {[string, string]} gradient - An array of two colors representing a gradient for the currency UI or display.
 * @property {string} unicode - The Unicode character representation of the currency (e.g. '$', '€').
 * @property {boolean} auto_rate - Indicates whether the currency supports auto rate conversion or not.
 * @property {boolean} [at_end] - Optional. Indicates whether the currency sign should be placed at the end of the amount.
 */
export type ICurrency = {
  code: string;
  enable: boolean;
  name: string;
  alt_name?: string;
  alt_factor?: number;
  country: string;
  flag?: string;
  icon?: string;
  floats: number;
  format: string;
  round_factor: number;
  gradient: [string, string];
  unicode: string;
  auto_rate: boolean;
  at_end?: boolean; // Force sign be at end
  blockchain?:boolean // Indicates whether the currency is a blockchain token or not.
};

const CurrencyRepository: Record<string, ICurrency> = {
  USD: {
    code: "USD",
    enable: true,
    name: "global.currency.USD",
    country: "USA",
    flag: "US",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#689F38", "#00796B"],
    unicode: "$",
    auto_rate: true, // Support auto rate by selldone.
  },

  EUR: {
    code: "EUR",
    enable: true,
    name: "global.currency.EUR",
    country: "Euro",
    flag: "EU",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#2038a2", "#464da8"],
    unicode: "€",
    auto_rate: false,
  },

  GBP: {
    code: "GBP",
    enable: true,
    name: "global.currency.GBP",
    country: "England",
    flag: "GB",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#D32F2F", "#E64A19"],
    unicode: "£",
    auto_rate: false,
  },

  CAD: {
    code: "CAD",
    enable: true,
    name: "global.currency.CAD",
    country: "Canada",
    flag: "CA",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#e01daf", "#b60d0d"],
    unicode: "$",
    auto_rate: false,
  },

  INR: {
    code: "INR",
    enable: true,
    name: "global.currency.INR",
    country: "India",
    flag: "IN",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#e39d24", "#0fce08"],
    unicode: "₹",
    auto_rate: false,
  },

  DKK: {
    code: "DKK",
    enable: true,
    name: "global.currency.DKK",
    country: "Denmark",
    flag: "DK",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#5724e3", "#3308ce"],
    unicode: "Kr",
    auto_rate: false,
  },

  IRR: {
    code: "IRR",
    enable: true,
    name: "global.currency.IRR",
    country: "ایران",
    flag: "IR",
    floats: 0,
    format: "0,0",
    alt_name: "global.currency.IRR_alt",
    alt_factor: 0.1,
    round_factor: 5000,
    gradient: ["#A0F9FF", "#7795FF"],
    unicode: "﷼",
    auto_rate: false,
  },

  TRY: {
    code: "TRY",
    enable: true,
    name: "global.currency.TRY",
    country: "Turkey",
    flag: "TR",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#db1c42", "#950505"],
    unicode: "₺",
    auto_rate: false,
  },

  CNY: {
    code: "CNY",
    enable: true,
    name: "global.currency.CNY",
    country: "China",
    flag: "CN",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ee4d6d", "#8b362b"],
    unicode: "¥",
    auto_rate: false,
  },

  RUB: {
    code: "RUB",
    enable: true,
    name: "global.currency.RUB",
    country: "Russia",
    flag: "RU",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#0c7ebb", "#11a4c7"],
    unicode: "₽",
    auto_rate: false,
  },

  PKR: {
    code: "PKR",
    enable: true,
    name: "global.currency.PKR",
    country: "Pakistan",
    flag: "PK",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1f8d4d", "#013f1b"],
    unicode: "₨",
    auto_rate: false,
  },

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  AED: {
    code: "AED",
    enable: true,
    name: "global.currency.AED",
    country: "Emirates",
    flag: "AE",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#2038a2", "#464da8"],
    unicode: "د.إ",
    auto_rate: false,
  },

  AFN: {
    code: "AFN",
    enable: true,
    name: "global.currency.AFN",
    country: "Afghanistan",
    flag: "AF",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#db1c42", "#d91290"],
    unicode: "؋",
    auto_rate: false,
  },

  SAR: {
    code: "SAR",
    enable: true,
    name: "global.currency.SAR",
    country: "Saudi Arabia",
    flag: "SA",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#a0d428", "#65b609"],
    unicode: "﷼",
    auto_rate: false,
  },

  SBD: {
    code: "SBD",
    enable: true,
    name: "global.currency.SBD",
    country: "Solomon Islands",
    flag: "SB",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1153b6", "#dab915"],
    unicode: "SRe",
    auto_rate: false,
  },
  SCR: {
    code: "SCR",
    enable: true,
    name: "global.currency.SCR",
    country: "Seychelles",
    flag: "SB",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1c66d3", "#498109"],
    unicode: "Si$",
    auto_rate: false,
  },

  QAR: {
    code: "QAR",
    enable: true,
    name: "global.currency.QAR",
    country: "Qatar",
    flag: "QA",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#7b3030", "#6a1512"],
    unicode: "ر.ق",
    auto_rate: false,
  },

  EGP: {
    code: "EGP",
    enable: true,
    name: "global.currency.EGP",
    country: "Egypt",
    flag: "EG",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#d48d23", "#cea408"],
    unicode: "جنيه", //"E£",
    auto_rate: false,
  },

  ALL: {
    code: "ALL",
    enable: true,
    name: "global.currency.ALL",
    country: "Albania",
    flag: "AL",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#990909", "#240101"],
    unicode: "L",
    auto_rate: false,
  },

  AMD: {
    code: "AMD",
    enable: true,
    name: "global.currency.AMD",
    country: "Armenia",
    flag: "AM",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#c21717", "#ea910a"],
    unicode: "֏",
    auto_rate: false,
  },

  ANG: {
    code: "ANG",
    enable: true,
    name: "global.currency.ANG",
    country: "Curaçao",
    flag: "CW",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#f1e113", "#012a7b"],
    unicode: "ƒ",
    auto_rate: false,
  },

  AOA: {
    code: "AOA",
    enable: true,
    name: "global.currency.AOA",
    country: "Angola",
    flag: "AO",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#c6092e", "#000000"],
    unicode: "Kz",
    auto_rate: false,
  },

  ARS: {
    code: "ARS",
    enable: true,
    name: "global.currency.ARS",
    country: "Argentina",
    flag: "AR",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#74acdf", "#42729e"],
    unicode: "$",
    auto_rate: false,
  },
  AUD: {
    code: "AUD",
    enable: true,
    name: "global.currency.AUD",
    country: "Australia",
    flag: "AU",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1b1bc2", "#02028b"],
    unicode: "$",
    auto_rate: false,
  },

  AWG: {
    code: "AWG",
    enable: true,
    name: "global.currency.AWG",
    country: "Aruba",
    flag: "AW",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#61a9f1", "#418fde"],
    unicode: "ƒ",
    auto_rate: false,
  },

  AZN: {
    code: "AZN",
    enable: true,
    name: "global.currency.AZN",
    country: "Azerbaijan",
    flag: "AZ",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#00b5e2", "#519e2f"],
    unicode: "₼",
    auto_rate: false,
  },

  BAM: {
    code: "BAM",
    enable: true,
    name: "global.currency.BAM",
    country: "Bosnia & Herzegovina",
    flag: "BA",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#fecb00", "#002395"],
    unicode: "KM",
    auto_rate: false,
  },

  BBD: {
    code: "BBD",
    enable: true,
    name: "global.currency.BBD",
    country: "Barbados",
    flag: "BB",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ffc726", "#00267f"],
    unicode: "Bds$",
    auto_rate: false,
  },

  BDT: {
    code: "BDT",
    enable: true,
    name: "global.currency.BDT",
    country: "Bangladesh",
    flag: "BD",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ec293f", "#00674c"],
    unicode: "৳",
    auto_rate: false,
  },

  BGN: {
    code: "BGN",
    enable: true,
    name: "global.currency.BGN",
    country: "Bulgaria",
    flag: "BG",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#00966e", "#d62612"],
    unicode: "лв.",
    auto_rate: false,
  },

  BIF: {
    code: "BIF",
    enable: true,
    name: "global.currency.BIF",
    country: "Burundi",
    flag: "BI",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ce1126", "#1eb53a"],
    unicode: "FBu",
    auto_rate: false,
  },

  BMD: {
    code: "BMD",
    enable: true,
    name: "global.currency.BMD",
    country: "Bermuda",
    flag: "BM",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ce1126", "#7d0a32"],
    unicode: "$",
    auto_rate: false,
  },

  BND: {
    code: "BND",
    enable: true,
    name: "global.currency.BND",
    country: "Brunei Darussalam",
    flag: "BN",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#f7e017", "#84770c"],
    unicode: "B$",
    auto_rate: false,
  },

  BOB: {
    code: "BOB",
    enable: true,
    name: "global.currency.BOB",
    country: "Bolivia",
    flag: "BO",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ce2a1d", "#f1dc00"],
    unicode: "Bs",
    auto_rate: false,
  },

  BRL: {
    code: "BRL",
    enable: true,
    name: "global.currency.BRL",
    country: "Brazil",
    flag: "BR",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#079547", "#f1a100"],
    unicode: "R$",
    auto_rate: false,
  },
  BSD: {
    code: "BSD",
    enable: true,
    name: "global.currency.BSD",
    country: "Bahamas",
    flag: "BS",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#00778b", "#05444e"],
    unicode: "B$",
    auto_rate: false,
  },
  BWP: {
    code: "BWP",
    enable: true,
    name: "global.currency.BWP",
    country: "Botswana",
    flag: "BW",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#6aa4cb", "#3a596d"],
    unicode: "P",
    auto_rate: false,
  },

  BYN: {
    code: "BYN",
    enable: true,
    name: "global.currency.BYN",
    country: "Belarus",
    flag: "BY",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#c8161f", "#009400"],
    unicode: "Br",
    auto_rate: false,
  },
  BZD: {
    code: "BZD",
    enable: true,
    name: "global.currency.BZD",
    country: "Belize",
    flag: "BZ",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#161591", "#0c0c45"],
    unicode: "$",
    auto_rate: false,
  },
  CDF: {
    code: "CDF",
    enable: true,
    name: "global.currency.CDF",
    country: "Congo",
    flag: "CD",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#007bf7", "#c80f20"],
    unicode: "FC",
    auto_rate: false,
  },
  CHF: {
    code: "CHF",
    enable: true,
    name: "global.currency.CHF",
    country: "Switzerland",
    flag: "CH",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1d1ca1", "#a01f2b"],
    unicode: "Fr",
    auto_rate: false,
  },

  CLP: {
    code: "CLP",
    enable: true,
    name: "global.currency.CLP",
    country: "Chile",
    flag: "CL",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#3949f3", "#551fa0"],
    unicode: "$",
    auto_rate: false,
  },
  COP: {
    code: "COP",
    enable: true,
    name: "global.currency.COP",
    country: "Colombia",
    flag: "CO",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#f3af39", "#c11f32"],
    unicode: "$",
    auto_rate: false,
  },

  CRC: {
    code: "CRC",
    enable: true,
    name: "global.currency.CRC",
    country: "Costa Rica",
    flag: "CR",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#002a7b", "#c81026"],
    unicode: "₡",
    auto_rate: false,
  },
  CVE: {
    code: "CVE",
    enable: true,
    name: "global.currency.CVE",
    country: "Cabo Verde",
    flag: "CV",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#002a7b", "#1094c8"],
    unicode: "$",
    auto_rate: false,
  },
  CZK: {
    code: "CZK",
    enable: true,
    name: "global.currency.CZK",
    country: "Czechia",
    flag: "CZ",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#8927f8", "#c8101f"],
    unicode: "Kč",
    auto_rate: false,
  },

  DJF: {
    code: "DJF",
    enable: true,
    name: "global.currency.DJF",
    country: "Djibouti",
    flag: "DJ",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#27e7f8", "#10c844"],
    unicode: "Fdj",
    auto_rate: false,
  },

  DOP: {
    code: "DOP",
    enable: true,
    name: "global.currency.DOP",
    country: "Dominican Republic",
    flag: "DO",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#2754f8", "#4710c8"],
    unicode: "$",
    auto_rate: false,
  },

  DZD: {
    code: "DZD",
    enable: true,
    name: "global.currency.DZD",
    country: "Algeria",
    flag: "DZ",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#006633", "#0b894a"],
    unicode: "دج",
    auto_rate: false,
  },

  ETB: {
    code: "ETB",
    enable: true,
    name: "global.currency.ETB",
    country: "Ethiopia",
    flag: "ET",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#47a536", "#d7ae13"],
    unicode: "Br",
    auto_rate: false,
  },
  FJD: {
    code: "FJD",
    enable: true,
    name: "global.currency.FJD",
    country: "Fiji",
    flag: "FJ",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#66ade0", "#3777a5"],
    unicode: "$",
    auto_rate: false,
  },
  FKP: {
    code: "FKP",
    enable: true,
    name: "global.currency.FKP",
    country: "Falkland Islands",
    flag: "FK",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#001b69", "#173792"],
    unicode: "£",
    auto_rate: false,
  },
  GEL: {
    code: "GEL",
    enable: true,
    name: "global.currency.GEL",
    country: "Georgia",
    flag: "GE",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#a10e0e", "#771792"],
    unicode: "ლ",
    auto_rate: false,
  },

  GIP: {
    code: "GIP",
    enable: true,
    name: "global.currency.GIP",
    country: "Gibraltar",
    flag: "GI",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#a10e0e", "#1d050c"],
    unicode: "£",
    auto_rate: false,
  },
  GMD: {
    code: "GMD",
    enable: true,
    name: "global.currency.GMD",
    country: "Gambia",
    flag: "GM",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#a10e0e", "#1e861f"],
    unicode: "D",
    auto_rate: false,
  },
  GNF: {
    code: "GNF",
    enable: true,
    name: "global.currency.GNF",
    country: "Guinea",
    flag: "GN",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#db2222", "#e3a913"],
    unicode: "Fr",
    auto_rate: false,
  },
  GTQ: {
    code: "GTQ",
    enable: true,
    name: "global.currency.GTQ",
    country: "Guatemala",
    flag: "GT",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#4997d0", "#2977af"],
    unicode: "Q",
    auto_rate: false,
  },
  GYD: {
    code: "GYD",
    enable: true,
    name: "global.currency.GYD",
    country: "Guyana",
    flag: "GY",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#009e49", "#fde26e"],
    unicode: "$",
    auto_rate: false,
  },

  GHS: {
    code: "GHS",
    enable: true,
    name: "global.currency.GHS",
    country: "Ghana",
    flag: "GH",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ff600d", "#e7b40c"],
    unicode: "GH₵",
    auto_rate: false,
  },

  HKD: {
    code: "HKD",
    enable: true,
    name: "global.currency.HKD",
    country: "Hong Kong",
    flag: "HK",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#af0c0c", "#930d34"],
    unicode: "$",
    auto_rate: false,
  },
  HNL: {
    code: "HNL",
    enable: true,
    name: "global.currency.HNL",
    country: "Honduras",
    flag: "HN",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#0d3994", "#0b2e76"],
    unicode: "L",
    auto_rate: false,
  },
  HRK: {
    code: "HRK",
    enable: true,
    name: "global.currency.HRK",
    country: "Croatia",
    flag: "HR",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#c11212", "#163d8d"],
    unicode: "kn",
    auto_rate: false,
  },
  HTG: {
    code: "HTG",
    enable: true,
    name: "global.currency.HTG",
    country: "Haiti",
    flag: "HT",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1229c1", "#af0413"],
    unicode: "G",
    auto_rate: false,
  },
  HUF: {
    code: "HUF",
    enable: true,
    name: "global.currency.HUF",
    country: "Hungary",
    flag: "HU",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#c11212", "#24b114"],
    unicode: "Ft",
    auto_rate: false,
  },
  IDR: {
    code: "IDR",
    enable: true,
    name: "global.currency.IDR",
    country: "Indonesia",
    flag: "ID",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#c11212", "#b1147d"],
    unicode: "Rp",
    auto_rate: false,
  },
  ILS: {
    code: "ILS",
    enable: true,
    name: "global.currency.ILS",
    country: "Israel",
    flag: "IL",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#0035b7", "#082d87"],
    unicode: "₪",
    auto_rate: false,
  },
  ISK: {
    code: "ISK",
    enable: true,
    name: "global.currency.ISK",
    country: "Iceland",
    flag: "IS",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1450e5", "#ff0c0c"],
    unicode: "kr",
    auto_rate: false,
  },
  JMD: {
    code: "JMD",
    enable: true,
    name: "global.currency.JMD",
    country: "Jamaica",
    flag: "JM",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#13ac1b", "#ffba0c"],
    unicode: "$",
    auto_rate: false,
  },
  JPY: {
    code: "JPY",
    enable: true,
    name: "global.currency.JPY",
    country: "Japan",
    flag: "JP",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ac1313", "#ff0c6d"],
    unicode: "¥",
    auto_rate: false,
  },
  KES: {
    code: "KES",
    enable: true,
    name: "global.currency.KES",
    country: "Kenya",
    flag: "KE",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#282828", "#ff0c0c"],
    unicode: "/-",
    auto_rate: false,
  },

  KGS: {
    code: "KGS",
    enable: true,
    name: "global.currency.KGS",
    country: "Kyrgyzstan",
    flag: "KG",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#a01111", "#ff8a0c"],
    unicode: "Лв",
    auto_rate: false,
  },
  KHR: {
    code: "KHR",
    enable: true,
    name: "global.currency.KHR",
    country: "Cambodia",
    flag: "KH",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#a01111", "#0c2cff"],
    unicode: "៛",
    auto_rate: false,
  },

  KMF: {
    code: "KMF",
    enable: true,
    name: "global.currency.KMF",
    country: "Comoros",
    flag: "KM",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#f1dd27", "#54ba1c"],
    unicode: "CF",
    auto_rate: false,
  },

  KRW: {
    code: "KRW",
    enable: true,
    name: "global.currency.KRW",
    country: "Korea",
    flag: "KR",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#2760f1", "#ba1c1c"],
    unicode: "₩",
    auto_rate: false,
  },

  KYD: {
    code: "KYD",
    enable: true,
    name: "global.currency.KYD",
    country: "Cayman Islands",
    flag: "KY",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#2760f1", "#0f3490"],
    unicode: "$",
    auto_rate: false,
  },
  KZT: {
    code: "KZT",
    enable: true,
    name: "global.currency.KZT",
    country: "Kazakhstan",
    flag: "KZ",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#10b5cb", "#21c7c7"],
    unicode: "₸",
    auto_rate: false,
  },
  LAK: {
    code: "LAK",
    enable: true,
    name: "global.currency.LAK",
    country: "Laos",
    flag: "LA",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#af10cb", "#920d37"],
    unicode: "₭",
    auto_rate: false,
  },
  LBP: {
    code: "LBP",
    enable: true,
    name: "global.currency.LBP",
    country: "Lebanon",
    flag: "LB",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#dc65c3", "#b60946"],
    unicode: "LL",
    auto_rate: false,
  },
  LKR: {
    code: "LKR",
    enable: true,
    name: "global.currency.LKR",
    country: "Sri Lanka",
    flag: "LK",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#cbb210", "#920d37"],
    unicode: "ரூ",
    auto_rate: false,
  },
  LRD: {
    code: "LRD",
    enable: true,
    name: "global.currency.LRD",
    country: "Liberia",
    flag: "LR",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#cb1096", "#c20e0e"],
    unicode: "L$",
    auto_rate: false,
  },
  LSL: {
    code: "LSL",
    enable: true,
    name: "global.currency.LSL",
    country: "Lesotho",
    flag: "LS",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#0e5ab6", "#5ab81f"],
    unicode: "L",
    auto_rate: false,
  },
  MAD: {
    code: "MAD",
    enable: true,
    name: "global.currency.MAD",
    country: "Morocco",
    flag: "MA",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#b60e0e", "#930a6c"],
    unicode: "د.م",
    auto_rate: false,
  },
  MGA: {
    code: "MGA",
    enable: true,
    name: "global.currency.MGA",
    country: "Madagascar",
    flag: "MG",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#0a9311", "#b60e0e"],
    unicode: "Ar",
    auto_rate: false,
  },
  MDL: {
    code: "MDL",
    enable: true,
    name: "global.currency.MDL",
    country: "Moldova",
    flag: "MD",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#d47714", "#1c50ec"],
    unicode: "L",
    auto_rate: false,
  },
  MKD: {
    code: "MKD",
    enable: true,
    name: "global.currency.MKD",
    country: "North Macedonia",
    flag: "MK",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#f8c40b", "#ec1c1c"],
    unicode: "Ден",
    auto_rate: false,
  },
  MMK: {
    code: "MMK",
    enable: true,
    name: "global.currency.MMK",
    country: "Myanmar",
    flag: "MM",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#f8c40b", "#2aec1c"],
    unicode: "K",
    auto_rate: false,
  },
  MNT: {
    code: "MNT",
    enable: true,
    name: "global.currency.MNT",
    country: "Mongolia",
    flag: "MN",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1187a5", "#c70909"],
    unicode: "₮",
    auto_rate: false,
  },
  MOP: {
    code: "MOP",
    enable: true,
    name: "global.currency.MOP",
    country: "Macao",
    flag: "MO",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1187a5", "#1c865c"],
    unicode: "M$",
    auto_rate: false,
  },
  MRO: {
    code: "MRO",
    enable: true,
    name: "global.currency.MRO",
    country: "Mauritania",
    flag: "MR",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#a51174", "#971212"],
    unicode: "UM",
    auto_rate: false,
  },
  MUR: {
    code: "MUR",
    enable: true,
    name: "global.currency.MUR",
    country: "Mauritania",
    flag: "MU",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#a51111", "#eabb14"],
    unicode: "₨",
    auto_rate: false,
  },
  MVR: {
    code: "MVR",
    enable: true,
    name: "global.currency.MVR",
    country: "Maldives",
    flag: "MV",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#a51111", "#138248"],
    unicode: "ރ",
    auto_rate: false,
  },
  MWK: {
    code: "MWK",
    enable: true,
    name: "global.currency.MWK",
    country: "Malawi",
    flag: "MW",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#100101", "#a80d0d"],
    unicode: "K",
    auto_rate: false,
  },
  MXN: {
    code: "MXN",
    enable: true,
    name: "global.currency.MXN",
    country: "Mexico",
    flag: "MX",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#139220", "#a80d0d"],
    unicode: "$",
    auto_rate: false,
  },
  MYR: {
    code: "MYR",
    enable: true,
    name: "global.currency.MYR",
    country: "Malaysia",
    flag: "MY",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#930993", "#a80d0d"],
    unicode: "RM",
    auto_rate: false,
  },
  MZN: {
    code: "MZN",
    enable: true,
    name: "global.currency.MZN",
    country: "Mozambique",
    flag: "MZ",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#09934c", "#ded310"],
    unicode: "MT",
    auto_rate: false,
  },
  NAD: {
    code: "NAD",
    enable: true,
    name: "global.currency.NAD",
    country: "Namibia",
    flag: "NA",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1745d0", "#138e32"],
    unicode: "$",
    auto_rate: false,
  },
  NGN: {
    code: "NGN",
    enable: true,
    name: "global.currency.NGN",
    country: "Nigeria",
    flag: "NG",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#5ac20e", "#138e32"],
    unicode: "₦",
    auto_rate: false,
  },
  NIO: {
    code: "NIO",
    enable: true,
    name: "global.currency.NIO",
    country: "Nicaragua",
    flag: "NI",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#0e89c2", "#0eb4a7"],
    unicode: "C$",
    auto_rate: false,
  },
  NOK: {
    code: "NOK",
    enable: true,
    name: "global.currency.NOK",
    country: "Norway",
    flag: "NO",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#c20e0e", "#740a2b"],
    unicode: "kr",
    auto_rate: false,
  },
  NPR: {
    code: "NPR",
    enable: true,
    name: "global.currency.NPR",
    country: "Nepal",
    flag: "NP",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#c20e0e", "#e74e7d"],
    unicode: "रू",
    auto_rate: false,
  },
  NZD: {
    code: "NZD",
    enable: true,
    name: "global.currency.NZD",
    country: "New Zealand",
    flag: "NZ",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#0e41c2", "#6a4ee7"],
    unicode: "$",
    auto_rate: false,
  },
  PAB: {
    code: "PAB",
    enable: true,
    name: "global.currency.PAB",
    country: "Panama",
    flag: "PA",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#000000", "#555555"],
    unicode: "B/.",
    auto_rate: false,
  },

  PEN: {
    code: "PEN",
    enable: true,
    name: "global.currency.PEN",
    country: "Peru",
    flag: "PE",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#bb1a1a", "#a81a1a"],
    unicode: "S/",
    auto_rate: false,
  },
  PGK: {
    code: "PGK",
    enable: true,
    name: "global.currency.PGK",
    country: "Papua New Guinea",
    flag: "PG",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#851414", "#170808"],
    unicode: "K",
    auto_rate: false,
  },

  PHP: {
    code: "PHP",
    enable: true,
    name: "global.currency.PHP",
    country: "Philippines",
    flag: "PH",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#5b22a1", "#1c5291"],
    unicode: "₱",
    auto_rate: false,
  },

  PLN: {
    code: "PLN",
    enable: true,
    name: "global.currency.PLN",
    country: "Poland",
    flag: "PL",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#a81678", "#e70d0d"],
    unicode: "zł",
    auto_rate: false,
  },
  PYG: {
    code: "PYG",
    enable: true,
    name: "global.currency.PYG",
    country: "Paraguay",
    flag: "PY",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#16a870", "#0867ab"],
    unicode: "₲",
    auto_rate: false,
  },

  RON: {
    code: "RON",
    enable: true,
    name: "global.currency.RON",
    country: "Romania",
    flag: "RO",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#cc970d", "#ab0808"],
    unicode: "lei",
    auto_rate: false,
  },
  RSD: {
    code: "RSD",
    enable: true,
    name: "global.currency.RSD",
    country: "Serbia",
    flag: "RS",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ab161b", "#3e08ab"],
    unicode: "din",
    auto_rate: false,
  },

  RWF: {
    code: "RWF",
    enable: true,
    name: "global.currency.RWF",
    country: "Rwanda",
    flag: "RS",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#23cad0", "#d7b818"],
    unicode: "R₣",
    auto_rate: false,
  },

  SGD: {
    code: "SGD",
    enable: true,
    name: "global.currency.SGD",
    country: "Singapore",
    flag: "SG",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#a10e0e", "#e70d0d"],
    unicode: "$",
    auto_rate: false,
  },
  SEK: {
    code: "SEK",
    enable: true,
    name: "global.currency.SEK",
    country: "Sweden",
    flag: "SE",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#f1b317", "#2e61b8"],
    unicode: "kr",
    auto_rate: false,
  },

  SHP: {
    code: "SHP",
    enable: true,
    name: "global.currency.SHP",
    country: "Saint Helena",
    flag: "SH",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#2f4d7b", "#2e61b8"],
    unicode: "£",
    auto_rate: false,
  },

  SLL: {
    code: "SLL",
    enable: true,
    name: "global.currency.SLL",
    country: "Sierra Leone",
    flag: "SL",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1b52a5", "#569716"],
    unicode: "Le",
    auto_rate: false,
  },

  SOS: {
    code: "SOS",
    enable: true,
    name: "global.currency.SOS",
    country: "Somalia",
    flag: "SO",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#3d7dde", "#1fd0b6"],
    unicode: "Sh",
    auto_rate: false,
  },

  SRD: {
    code: "SRD",
    enable: true,
    name: "global.currency.SRD",
    country: "Suriname",
    flag: "SR",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#3ba040", "#d94f18"],
    unicode: "$",
    auto_rate: false,
  },
  STD: {
    code: "STD",
    enable: true,
    name: "global.currency.STD",
    country: "Sao Tome and Principe",
    flag: "ST",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#3ba040", "#d99918"],
    unicode: "Db",
    auto_rate: false,
  },
  SZL: {
    code: "SZL",
    enable: true,
    name: "global.currency.SZL",
    country: "Eswatini",
    flag: "SZ",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#3b80a0", "#d99918"],
    unicode: "E",
    auto_rate: false,
  },

  THB: {
    code: "THB",
    enable: true,
    name: "global.currency.THB",
    country: "Thailand",
    flag: "TH",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#a03b3e", "#13379a"],
    unicode: "฿",
    auto_rate: false,
  },
  TJS: {
    code: "TJS",
    enable: true,
    name: "global.currency.TJS",
    country: "Tajikistan",
    flag: "TJ",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ad1116", "#138038"],
    unicode: "ЅM",
    auto_rate: false,
  },
  TOP: {
    code: "TOP",
    enable: true,
    name: "global.currency.TOP",
    country: "Tonga",
    flag: "TO",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ad1116", "#801346"],
    unicode: "T$",
    auto_rate: false,
  },

  TTD: {
    code: "TTD",
    enable: true,
    name: "global.currency.TTD",
    country: "Trinidad and Tobago",
    flag: "TT",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#e73636", "#820e1d"],
    unicode: "$",
    auto_rate: false,
  },
  TWD: {
    code: "TWD",
    enable: true,
    name: "global.currency.TWD",
    country: "Taiwan",
    flag: "TW",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#3c36e7", "#e73636"],
    unicode: "$",
    auto_rate: false,
  },
  TZS: {
    code: "TZS",
    enable: true,
    name: "global.currency.TZS",
    country: "Tanzania",
    flag: "TZ",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#3cac4e", "#2ad4bf"],
    unicode: "TSh",
    auto_rate: false,
  },

  UAH: {
    code: "UAH",
    enable: true,
    name: "global.currency.UAH",
    country: "Ukraine",
    flag: "UA",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#3c72ac", "#d4af2a"],
    unicode: "₴",
    auto_rate: false,
  },
  UGX: {
    code: "UGX",
    enable: true,
    name: "global.currency.UGX",
    country: "Uganda",
    flag: "UG",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#0c1015", "#d4af2a"],
    unicode: "USh",
    auto_rate: false,
  },

  UYU: {
    code: "UYU",
    enable: true,
    name: "global.currency.UYU",
    country: "Uruguay",
    flag: "UY",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1f61b4", "#1530b8"],
    unicode: "$",
    auto_rate: false,
  },

  UZS: {
    code: "UZS",
    enable: true,
    name: "global.currency.UZS",
    country: "Uzbekistan",
    flag: "UZ",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#498bde", "#3ad24f"],
    unicode: "so'm",
    auto_rate: false,
  },

  VND: {
    code: "VND",
    enable: true,
    name: "global.currency.VND",
    country: "Viet Nam",
    flag: "VN",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#b41f1f", "#d9bb28"],
    unicode: "₫",
    auto_rate: false,
  },
  VUV: {
    code: "VUV",
    enable: true,
    name: "global.currency.VUV",
    country: "Vanuatu",
    flag: "VU",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#b41f1f", "#151203"],
    unicode: "VT",
    auto_rate: false,
  },
  WST: {
    code: "WST",
    enable: true,
    name: "global.currency.WST",
    country: "Samoa",
    flag: "WS",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#b41f1f", "#2151d0"],
    unicode: "$",
    auto_rate: false,
  },
  XAF: {
    code: "XAF",
    enable: true,
    name: "global.currency.XAF",
    country: "Cameroon",
    flag: "CM",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1fb444", "#d0aa21"],
    unicode: "FCFA",
    auto_rate: false,
  },

  XCD: {
    code: "XCD",
    enable: true,
    name: "global.currency.XCD",
    country: "Dominica",
    flag: "DM",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1fb444", "#155f1e"],
    unicode: "$",
    auto_rate: false,
  },
  XOF: {
    code: "XOF",
    enable: true,
    name: "global.currency.XOF",
    country: "Guinea-Bissau",
    flag: "GW",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1fb444", "#c4a816"],
    unicode: "CFA",
    auto_rate: false,
  },
  XPF: {
    code: "XPF",
    enable: true,
    name: "global.currency.XPF",
    country: "New Caledonia",
    flag: "NC",
    floats: 0,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#567f15", "#1664c4"],
    unicode: "₣",
    auto_rate: false,
  },

  YER: {
    code: "YER",
    enable: true,
    name: "global.currency.YER",
    country: "Yemen",
    flag: "YE",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#a01919", "#050000"],
    unicode: "﷼",
    auto_rate: false,
  },
  ZAR: {
    code: "ZAR",
    enable: true,
    name: "global.currency.ZAR",
    country: "South Africa",
    flag: "ZA",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#26b445", "#d01818"],
    unicode: "R",
    auto_rate: false,
  },

  ZWD: {
    code: "ZWD",
    enable: true,
    name: "global.currency.ZWD",
    country: "Zimbabwe",
    flag: "ZW",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#26b445", "#d0b118"],
    unicode: "$",
    auto_rate: false,
  },

  ZMW: {
    code: "ZMW",
    enable: true,
    name: "global.currency.ZMW",
    country: "Zambia",
    flag: "ZM",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#26b445", "#26cda6"],
    unicode: "ZK",
    auto_rate: false,
  },

  IQD: {
    code: "IQD",
    enable: true,
    name: "global.currency.IQD",
    country: "Iraq",
    flag: "IQ",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#7a2323", "#020907"],
    unicode: "د.ع",
    auto_rate: false,
  },

  SYP: {
    code: "SYP",
    enable: true,
    name: "global.currency.SYP",
    country: "Syria",
    flag: "SY",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#7a2323", "#020907"],
    unicode: "£S",
    auto_rate: false,
  },

  OMR: {
    code: "OMR",
    enable: true,
    name: "OMR",
    country: "Oman",
    flag: "OM",
    floats: 3,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#1fb74a", "#49770d"],
    unicode: "ر.ع.",
    auto_rate: false,
  },

  // Add KWD
  KWD: {
    code: "KWD",
    enable: true,
    name: "KWD",
    country: "Kuwait",
    flag: "KW",
    floats: 3,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#b71f1f", "#770d42"],
    unicode: "د.ك",
    auto_rate: false,
  },

  BTN: {
    code: "BTN",
    enable: true,
    name: "BTN",
    country: "Bhutan",
    flag: "BT",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ffc813", "#d3600e"],
    unicode: "Nu.",
    auto_rate: false,
  },
  JOD: {
    code: "JOD",
    enable: true,
    name: "JOD",
    country: "Jordan",
    flag: "JO",
    floats: 2,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ff1313", "#800e0e"],
    unicode: "د.أ",
    auto_rate: false,
  },

  BHD: {
    code: "BHD",
    enable: true,
    name: "BHD",
    country: "Bahrain",
    flag: "BH",
    floats: 3,
    format: "0,0.00",
    round_factor: 100,
    gradient: ["#ff1313", "#771616"],
    unicode: "د.ب",
    auto_rate: false,
  },

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  ADA: {
    code: "ADA",
    enable: true,
    name: "global.currency.ADA",
    country: "Blockchain",
    icon: adaIcon,
    floats: 6,
    format: "0,0.[000000]",
    round_factor: 1,
    gradient: ["#55aae2", "#00a6ff"],
    unicode: "₳",
    auto_rate: true,
    blockchain:true
  },

  BTC: {
    code: "BTC",
    enable: true,
    name: "global.currency.BTC",
    country: "Blockchain",
    icon: btcIcon,
    floats: 6,
    format: "0,0.[000000]",
    round_factor: 1,
    gradient: ["#FBC02D", "#FFA000"],
    unicode: "₿",
    auto_rate: false,
    blockchain:true

  },
  ETH: {
    code: "ETH",
    enable: false,
    name: "global.currency.ETH",
    country: "Blockchain",
    icon: ethIcon,
    floats: 6,
    format: "0,0.[000000]",
    round_factor: 1,
    gradient: ["#607D8B", "#455A64"],
    unicode: "Ξ",
    auto_rate: false,
    blockchain:true

  },

  NIOC: {
    code: "NIOC",
    enable: false,
    name: "global.currency.NIOC",
    country: "Blockchain",
    icon: nioIcon,
    floats: 4,
    format: "0,0.[0000]",
    round_factor: 1,
    gradient: ["#C2185B", "#D32F2F"],
    unicode: "ṉ",
    auto_rate: false,
    blockchain:true

  },
  SEL: {
    code: "SEL",
    enable: true,
    name: "global.currency.SEL",
    country: "Blockchain",
    icon: selldoneIcon,
    floats: 4,
    format: "0,0.[0000]",
    round_factor: 1,
    gradient: ["#7B1FA2", "#512DA8"],
    unicode: "ഗ",
    auto_rate: false,
    blockchain:true

  },

  SOL: {
    code: "SOL",
    enable: true,
    name: "SOL",
    country: "Blockchain",
    icon: solanaIcon, // Replace with the appropriate icon variable
    floats: 9,
    format: "0,0.[000000000]",
    round_factor: 1,
    gradient: ["#4E44CE", "#00FFA3"], // Solana brand colors
    unicode: "ᔑ", // Replace with a unique symbol if desired
    auto_rate: false,
    blockchain:true

  },
};

// Convert the object into an array of entries, sort by key, and reconstruct it into an object
export const Currency: Record<string, ICurrency> = Object.entries(
  CurrencyRepository,
)
  .sort(([key1], [key2]) => key1.localeCompare(key2))
  .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
